/**
 * Safe math expression evaluator that replaces eval().
 * Supports: +, -, *, /, parentheses, and decimal numbers.
 */
export function safeEvaluate(expression: string): number {
  // Validate: only allow digits, operators, parentheses, dots, and whitespace
  if (!/^[\d+\-*/().e\s]+$/i.test(expression)) {
    throw new Error("Invalid expression");
  }

  const tokens = tokenize(expression);
  const result = parseExpression(tokens, { pos: 0 });

  if (result.pos < tokens.length) {
    throw new Error("Unexpected token");
  }

  return result.value;
}

type Token = { type: "number"; value: number } | { type: "op"; value: string };

function tokenize(expr: string): Token[] {
  const tokens: Token[] = [];
  let i = 0;
  const s = expr.replace(/\s/g, "");

  while (i < s.length) {
    // Handle negative numbers at start or after opening paren or operator
    if (
      s[i] === "-" &&
      (tokens.length === 0 ||
        (tokens[tokens.length - 1].type === "op" && tokens[tokens.length - 1].value !== ")"))
    ) {
      let num = "-";
      i++;
      while (i < s.length && (/\d/.test(s[i]) || s[i] === "." || s[i].toLowerCase() === "e")) {
        num += s[i++];
      }
      if (num === "-") throw new Error("Invalid expression");
      tokens.push({ type: "number", value: parseFloat(num) });
      continue;
    }

    if (/\d/.test(s[i]) || s[i] === ".") {
      let num = "";
      while (i < s.length && (/\d/.test(s[i]) || s[i] === "." || s[i].toLowerCase() === "e")) {
        num += s[i++];
      }
      tokens.push({ type: "number", value: parseFloat(num) });
    } else if ("+-*/()".includes(s[i])) {
      tokens.push({ type: "op", value: s[i] });
      i++;
    } else {
      throw new Error("Invalid character: " + s[i]);
    }
  }

  return tokens;
}

function parseExpression(tokens: Token[], ctx: { pos: number }): { value: number; pos: number } {
  let left = parseTerm(tokens, ctx);

  while (ctx.pos < tokens.length && tokens[ctx.pos].type === "op" && (tokens[ctx.pos].value === "+" || tokens[ctx.pos].value === "-")) {
    const op = tokens[ctx.pos].value;
    ctx.pos++;
    const right = parseTerm(tokens, ctx);
    left.value = op === "+" ? left.value + right.value : left.value - right.value;
  }

  return left;
}

function parseTerm(tokens: Token[], ctx: { pos: number }): { value: number; pos: number } {
  let left = parseFactor(tokens, ctx);

  while (ctx.pos < tokens.length && tokens[ctx.pos].type === "op" && (tokens[ctx.pos].value === "*" || tokens[ctx.pos].value === "/")) {
    const op = tokens[ctx.pos].value;
    ctx.pos++;
    const right = parseFactor(tokens, ctx);
    left.value = op === "*" ? left.value * right.value : left.value / right.value;
  }

  return left;
}

function parseFactor(tokens: Token[], ctx: { pos: number }): { value: number; pos: number } {
  if (ctx.pos >= tokens.length) throw new Error("Unexpected end");

  const token = tokens[ctx.pos];

  if (token.type === "number") {
    ctx.pos++;
    return { value: token.value, pos: ctx.pos };
  }

  if (token.type === "op" && token.value === "(") {
    ctx.pos++;
    const result = parseExpression(tokens, ctx);
    if (ctx.pos >= tokens.length || tokens[ctx.pos].value !== ")") {
      throw new Error("Missing closing parenthesis");
    }
    ctx.pos++;
    return result;
  }

  throw new Error("Unexpected token");
}
